import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { Profile } from "../types";
import {
  Aside,
  AsideCard,
  Badge,
  Botao,
  BotaoPequeno,
  CabecalhoDaSecao,
  Check,
  Coluna,
  EditorGrid,
  Hint,
  Lab,
  Tip,
} from "./EditorChrome";
import {
  INSTAGRAM_PENDING_KEY,
  INSTAGRAM_RETURN_KEY,
  instagramApi,
  type InstagramConnection,
  type InstagramMediaItem,
  type InstagramOverview,
  type InstagramPreviewMedia,
} from "../../../services/instagram";

type Tom = "azul" | "verde" | "amarelo" | "cinza" | "vermelho";

const CONNECTION_STATUS: Record<string, { label: string; tom: Tom }> = {
  ACTIVE: { label: "Conectado", tom: "verde" },
  TOKEN_EXPIRED: { label: "Token expirado", tom: "amarelo" },
  PERMISSION_DENIED: { label: "Ação necessária", tom: "amarelo" },
  REVOKED: { label: "Acesso removido no Instagram", tom: "vermelho" },
  DISCONNECTED: { label: "Desconectado", tom: "cinza" },
};

const ITEM_STATUS: Record<string, { label: string; tom: Tom }> = {
  PENDING_REVIEW: { label: "Aguardando aprovação", tom: "azul" },
  APPROVED: { label: "Aprovado", tom: "azul" },
  PUBLISHING: { label: "Enviando ao Google", tom: "azul" },
  PUBLISHED: { label: "Criado no Google", tom: "verde" },
  SKIPPED: { label: "Ignorado", tom: "cinza" },
  REJECTED: { label: "Descartado", tom: "cinza" },
  FAILED: { label: "Falhou", tom: "vermelho" },
};

const SKIP_REASON: Record<string, string> = {
  VIDEO_OU_REELS: "Vídeo ou Reels não vai para o Google nesta versão.",
  SEM_FOTO: "Publicação sem foto compatível.",
  TEXTO_INSUFICIENTE: "Legenda curta demais: escreva o texto antes de aprovar.",
  DISCONNECTED: "Instagram desconectado.",
  REVOKED: "Acesso removido pelo dono da conta.",
  DATA_DELETION: "Dados apagados a pedido do dono da conta.",
};

const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const Selo: React.FC<{ tom: Tom; children: React.ReactNode }> = ({ tom, children }) =>
  tom === "vermelho" ? (
    <Badge style={{ background: "#FCE8E6", color: "#B3261E" }}>{children}</Badge>
  ) : (
    <Badge $tom={tom}>{children}</Badge>
  );

const Cartao = styled.section`
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

const Linha = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 12px;
  min-width: 0;
`;

const Avatar = styled.span`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eaf1fb;
  color: #1264c7;
  .material-icons {
    font-size: 24px;
  }
`;

const Nome = styled.strong`
  font-family: var(--font-sans);
  font-size: 16px;
  font-weight: 500;
  color: var(--ink);
  overflow-wrap: anywhere;
`;

const Divisor = styled.div`
  border-top: 1px solid #eef1f4;
`;

const Toast = styled.div<{ $type: "error" | "success" }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 13px;
  background: ${(p) => (p.$type === "error" ? "#fce8e6" : "#eaf7ee")};
  color: ${(p) => (p.$type === "error" ? "#b3261e" : "#137333")};
  .material-icons {
    font-size: 18px;
  }
`;

const Interruptor = styled.button<{ $on: boolean }>`
  width: 36px;
  height: 20px;
  border-radius: 999px;
  background: ${(p) => (p.$on ? "#1264C7" : "#BDC1C6")};
  position: relative;
  display: inline-block;
  border: 0;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: ${(p) => (p.$on ? "18px" : "2px")};
    width: 16px;
    height: 16px;
    border-radius: 999px;
    background: #fff;
    transition: left 0.15s;
  }
  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

const Consentimento = styled.label`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 13px;
  line-height: 19px;
  color: #3c4043;
  background: #f8f9fa;
  border-radius: 14px;
  padding: 14px 16px;
  cursor: pointer;
  input {
    margin-top: 2px;
    width: 16px;
    height: 16px;
    accent-color: #1264c7;
  }
`;

const Seletor = styled.select`
  height: 36px;
  padding: 0 10px;
  border-radius: 10px;
  border: 1px solid #dadce0;
  font-family: var(--font-sans);
  font-size: 13px;
  background: #fff;
`;

const Grade = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
`;

const Miniatura = styled.a<{ $apagada?: boolean }>`
  display: block;
  border: 1px solid var(--line);
  border-radius: 12px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  opacity: ${(p) => (p.$apagada ? 0.55 : 1)};
  img {
    display: block;
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    background: #f1f3f4;
  }
  div {
    padding: 8px 10px;
    font-size: 12px;
    line-height: 16px;
    color: #5f6368;
  }
`;

const ItemFila = styled.div`
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  gap: 16px;
  padding-top: 16px;
  border-top: 1px solid #eef1f4;
  &:first-of-type {
    border-top: 0;
    padding-top: 0;
  }
  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
  img {
    width: 96px;
    height: 96px;
    object-fit: cover;
    border-radius: 12px;
    background: #f1f3f4;
  }
`;

const Colunas = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  @media (max-width: 800px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Legenda = styled.div`
  font-size: 13px;
  line-height: 19px;
  color: #3c4043;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 10px 12px;
  white-space: pre-wrap;
  max-height: 168px;
  overflow: auto;
`;

const Texto = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 168px;
  border: 1px solid #dadce0;
  border-radius: 12px;
  padding: 10px 12px;
  font-family: var(--font-sans);
  font-size: 13px;
  line-height: 19px;
  color: var(--text);
  resize: vertical;
  &:focus {
    outline: 2px solid #d6e4f7;
    border-color: #1264c7;
  }
`;

const LinhaHistorico = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 10px 0;
  border-top: 1px solid #eef1f4;
  font-size: 13px;
  color: #3c4043;
  &:first-of-type {
    border-top: 0;
  }
  a {
    color: #1264c7;
    font-weight: 500;
    text-decoration: none;
  }
`;

interface Props {
  profile: Profile;
}

export const InstagramTab: React.FC<Props> = ({ profile }) => {
  const profileId = profile.id;
  const [overview, setOverview] = useState<InstagramOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [consentFor, setConsentFor] = useState<number | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [preview, setPreview] = useState<Record<number, InstagramPreviewMedia[]>>({});
  const [drafts, setDrafts] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    try {
      setError(null);
      setOverview(await instagramApi.overview(profileId));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const connect = () =>
    run("connect", async () => {
      const url = await instagramApi.start(profileId);
      try {
        sessionStorage.setItem(INSTAGRAM_PENDING_KEY, profileId);
        sessionStorage.setItem(INSTAGRAM_RETURN_KEY, window.location.pathname + window.location.search);
      } catch {
        // sessionStorage bloqueado: o callback volta para a lista de perfis
      }
      window.location.assign(url);
    });

  const disconnect = (c: InstagramConnection) => {
    if (!window.confirm(`Desconectar @${c.username ?? "instagram"}? A sincronização para na hora.`)) return;
    run(`disc-${c.id}`, async () => {
      await instagramApi.disconnect(profileId, c.id);
      setNotice("Instagram desconectado.");
      await load();
    });
  };

  const enableSync = (c: InstagramConnection) =>
    run(`sync-${c.id}`, async () => {
      await instagramApi.updateSettings(profileId, c.id, {
        syncEnabled: true,
        consentAccepted: true,
        consentTextVersion: overview?.consentTextVersion,
      });
      setConsentFor(null);
      setConsentChecked(false);
      setNotice("Sincronização ativada. Só publicações feitas a partir de agora entram na fila.");
      await load();
    });

  const pauseSync = (c: InstagramConnection) =>
    run(`sync-${c.id}`, async () => {
      await instagramApi.updateSettings(profileId, c.id, { syncEnabled: false });
      setNotice("Sincronização pausada.");
      await load();
    });

  const changeLimit = (c: InstagramConnection, value: number) =>
    run(`limit-${c.id}`, async () => {
      await instagramApi.updateSettings(profileId, c.id, { maxPostsPerDay: value });
      await load();
    });

  const loadPreview = (c: InstagramConnection) =>
    run(`prev-${c.id}`, async () => {
      const list = await instagramApi.preview(profileId, c.id);
      setPreview((p) => ({ ...p, [c.id]: list }));
    });

  const saveDraft = (item: InstagramMediaItem) =>
    run(`save-${item.id}`, async () => {
      await instagramApi.editSummary(profileId, item.id, drafts[item.id] ?? item.summary ?? "");
      setDrafts((d) => {
        const n = { ...d };
        delete n[item.id];
        return n;
      });
      await load();
    });

  const approve = (item: InstagramMediaItem) =>
    run(`appr-${item.id}`, async () => {
      if (drafts[item.id] !== undefined) {
        await instagramApi.editSummary(profileId, item.id, drafts[item.id]);
      }
      const r = await instagramApi.approve(profileId, item.id);
      setNotice(
        r.status === "PUBLISHED"
          ? "Postagem criada no Google. Pode levar alguns minutos para aparecer no perfil."
          : r.errorMessage ?? "O Google não confirmou a postagem.",
      );
      await load();
    });

  const reject = (item: InstagramMediaItem) =>
    run(`rej-${item.id}`, async () => {
      await instagramApi.reject(profileId, item.id);
      await load();
    });

  const ready = !!overview?.integrationEnabled && !!overview?.oauthConfigured;
  const connections = overview?.connections ?? [];
  const emAberto = ["PENDING_REVIEW", "FAILED", "PUBLISHING"];
  const queue = (overview?.items ?? []).filter((i) => emAberto.includes(i.status));
  const history = (overview?.items ?? []).filter((i) => !emAberto.includes(i.status));
  const ativa = connections.find((c) => c.status === "ACTIVE");
  const seloDoCabecalho = !ready
    ? { label: "Em preparação", tom: "amarelo" as Tom, icon: "schedule" }
    : ativa
      ? { label: `@${ativa.username ?? "instagram"} conectado`, tom: "verde" as Tom, icon: "check_circle" }
      : { label: "Sem conta conectada", tom: "cinza" as Tom, icon: "link_off" };

  return (
    <EditorGrid>
      <Coluna style={{ gap: 18 }}>
        <CabecalhoDaSecao
          titulo="Instagram"
          apoio="Novas fotos e carrosséis da conta profissional viram postagens no Perfil da Empresa no Google, sempre com aprovação antes de publicar."
          extra={
            !loading && (
              <Selo tom={seloDoCabecalho.tom}>
                <span className="material-icons">{seloDoCabecalho.icon}</span>
                {seloDoCabecalho.label}
              </Selo>
            )
          }
        />

        {error && (
          <Toast $type="error" role="alert">
            <span className="material-icons">error_outline</span>
            <span>{error}</span>
          </Toast>
        )}
        {notice && (
          <Toast $type="success" role="status">
            <span className="material-icons">check_circle</span>
            <span>{notice}</span>
          </Toast>
        )}

        {loading && <Hint>Carregando…</Hint>}

        {!loading && !ready && (
          <Tip $tom="aviso">
            <span className="material-icons">schedule</span>
            <span>
              Integração em preparação: aguardando a liberação da Meta. A conexão fica disponível
              assim que a GeoRanking for aprovada.
            </span>
          </Tip>
        )}

        {!loading && ready && connections.length === 0 && (
          <Cartao>
            <Linha>
              <Avatar>
                <span className="material-icons">photo_camera</span>
              </Avatar>
              <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: "1 1 220px" }}>
                <Nome>Nenhuma conta conectada</Nome>
                <Hint>
                  Precisa ser uma conta profissional (Comercial ou Criador de conteúdo). Conta pessoal
                  não funciona.
                </Hint>
              </div>
              <Botao type="button" $variante="filled" onClick={connect} disabled={busy === "connect"}>
                <span className="material-icons">link</span>
                {busy === "connect" ? "Abrindo o Instagram…" : "Conectar Instagram"}
              </Botao>
            </Linha>
          </Cartao>
        )}

        {!loading &&
          ready &&
          connections.map((c) => {
            const st = CONNECTION_STATUS[c.status] ?? { label: c.status, tom: "cinza" as Tom };
            const precisaReconectar = c.status !== "ACTIVE";
            const ligado = c.syncEnabled || consentFor === c.id;
            return (
              <Cartao key={c.id}>
                <Linha>
                  <Avatar>
                    <span className="material-icons">photo_camera</span>
                  </Avatar>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0, flex: "1 1 220px" }}>
                    <Linha style={{ gap: 8 }}>
                      <Nome>@{c.username ?? "instagram"}</Nome>
                      <Selo tom={st.tom}>{st.label}</Selo>
                    </Linha>
                    <Hint>
                      Última verificação {formatDate(c.lastCheckedAt)} · acesso válido até{" "}
                      {formatDate(c.tokenExpiresAt)}
                      {c.activatedAt && <> · ativado em {formatDate(c.activatedAt)}</>}
                    </Hint>
                    {c.lastError && <Hint style={{ color: "#B3261E" }}>{c.lastError}</Hint>}
                  </div>
                  {precisaReconectar ? (
                    <BotaoPequeno type="button" $variante="filled" onClick={connect} disabled={!!busy}>
                      <span className="material-icons">refresh</span> Reconectar
                    </BotaoPequeno>
                  ) : (
                    <BotaoPequeno type="button" $variante="outlined" onClick={() => loadPreview(c)} disabled={!!busy}>
                      <span className="material-icons">photo_library</span>
                      {busy === `prev-${c.id}` ? "Lendo…" : "Ver últimas publicações"}
                    </BotaoPequeno>
                  )}
                  <BotaoPequeno type="button" $variante="text" onClick={() => disconnect(c)} disabled={!!busy}>
                    <span className="material-icons">link_off</span> Desconectar
                  </BotaoPequeno>
                </Linha>

                {!precisaReconectar && (
                  <>
                    <Divisor />
                    <Linha>
                      <Interruptor
                        type="button"
                        role="switch"
                        aria-checked={ligado}
                        aria-label="Levar novas publicações para o Google"
                        $on={ligado}
                        disabled={!!busy || !overview?.syncEnabled}
                        onClick={() => {
                          if (!ligado) {
                            setConsentFor(c.id);
                            setConsentChecked(false);
                          } else if (c.syncEnabled) {
                            pauseSync(c);
                          } else {
                            setConsentFor(null);
                          }
                        }}
                      />
                      <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: "1 1 220px", minWidth: 0 }}>
                        <span style={{ fontSize: 14, color: "var(--ink)" }}>Levar novas publicações para o Google</span>
                        <Hint>
                          {overview?.syncEnabled
                            ? "Aprovação humana antes de cada postagem."
                            : "A leitura automática ainda não foi liberada no servidor."}
                        </Hint>
                      </div>
                      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#5f6368" }}>
                        Máx. por dia
                        <Seletor
                          value={c.maxPostsPerDay}
                          disabled={!!busy}
                          onChange={(e) => changeLimit(c, Number(e.target.value))}
                        >
                          {[1, 2, 3].map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </Seletor>
                      </label>
                    </Linha>

                    {consentFor === c.id && !c.syncEnabled && (
                      <>
                        <Consentimento>
                          <input
                            type="checkbox"
                            checked={consentChecked}
                            onChange={(e) => setConsentChecked(e.target.checked)}
                          />
                          <span>{overview?.consentText}</span>
                        </Consentimento>
                        <Linha>
                          <BotaoPequeno
                            type="button"
                            $variante="filled"
                            disabled={!consentChecked || !!busy}
                            onClick={() => enableSync(c)}
                          >
                            <span className="material-icons">check</span> Ativar
                          </BotaoPequeno>
                          <BotaoPequeno type="button" $variante="text" onClick={() => setConsentFor(null)}>
                            Cancelar
                          </BotaoPequeno>
                        </Linha>
                      </>
                    )}
                  </>
                )}

                {preview[c.id] && (
                  <>
                    <Divisor />
                    <Lab>
                      <span className="material-icons">photo_library</span>Últimas publicações
                    </Lab>
                    {preview[c.id].length === 0 && <Hint>Nenhuma publicação encontrada.</Hint>}
                    <Grade>
                      {preview[c.id].map((m) => (
                        <Miniatura
                          key={m.igMediaId}
                          href={m.permalink ?? undefined}
                          target="_blank"
                          rel="noreferrer"
                          $apagada={!m.eligible}
                          title={m.caption ?? ""}
                        >
                          {m.thumbnailUrl ? <img src={m.thumbnailUrl} alt="" loading="lazy" /> : <img alt="" />}
                          <div>
                            {formatDate(m.timestamp)}
                            <br />
                            {m.eligible
                              ? "Pode ir para o Google"
                              : SKIP_REASON[m.ineligibleReason ?? ""] ?? "Não compatível"}
                          </div>
                        </Miniatura>
                      ))}
                    </Grade>
                  </>
                )}
              </Cartao>
            );
          })}

        {!loading && ready && queue.length > 0 && (
          <Cartao>
            <Linha>
              <Lab>
                <span className="material-icons">fact_check</span>Fila de aprovação
              </Lab>
              <Selo tom="azul">{queue.length}</Selo>
            </Linha>
            {!overview?.publishEnabled && (
              <Tip>
                <span className="material-icons">info</span>
                <span>A publicação no Google ainda não foi liberada: dá para revisar, não para publicar.</span>
              </Tip>
            )}
            {queue.map((item) => {
              const st = ITEM_STATUS[item.status];
              const text = drafts[item.id] ?? item.summary ?? "";
              return (
                <ItemFila key={item.id}>
                  <a href={item.permalink ?? undefined} target="_blank" rel="noreferrer">
                    {item.imageUrl ? <img src={item.imageUrl} alt="" /> : <img alt="" />}
                  </a>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
                    <Linha style={{ gap: 8 }}>
                      <Selo tom={st.tom}>{st.label}</Selo>
                      <Hint>Publicado no Instagram em {formatDate(item.mediaTimestamp)}</Hint>
                    </Linha>
                    {item.errorMessage && <Hint style={{ color: "#B3261E" }}>{item.errorMessage}</Hint>}
                    {item.skipReason && <Hint>{SKIP_REASON[item.skipReason] ?? item.skipReason}</Hint>}
                    <Colunas>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                        <Lab>Legenda no Instagram</Lab>
                        <Legenda>{item.caption || "—"}</Legenda>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                        <Lab>Texto para o Google ({text.length}/1500)</Lab>
                        <Texto
                          value={text}
                          maxLength={1500}
                          aria-label="Texto para o Google"
                          onChange={(e) => setDrafts((d) => ({ ...d, [item.id]: e.target.value }))}
                        />
                      </div>
                    </Colunas>
                    <Linha>
                      <BotaoPequeno
                        type="button"
                        $variante="filled"
                        disabled={!!busy || !overview?.publishEnabled || text.trim().length < 20}
                        onClick={() => approve(item)}
                      >
                        <span className="material-icons">send</span>
                        {busy === `appr-${item.id}` ? "Enviando…" : "Aprovar e publicar"}
                      </BotaoPequeno>
                      {drafts[item.id] !== undefined && (
                        <BotaoPequeno type="button" $variante="outlined" disabled={!!busy} onClick={() => saveDraft(item)}>
                          Salvar texto
                        </BotaoPequeno>
                      )}
                      <BotaoPequeno type="button" $variante="text" disabled={!!busy} onClick={() => reject(item)}>
                        Descartar
                      </BotaoPequeno>
                    </Linha>
                  </div>
                </ItemFila>
              );
            })}
          </Cartao>
        )}

        {!loading && ready && history.length > 0 && (
          <Cartao>
            <Lab>
              <span className="material-icons">history</span>Histórico
            </Lab>
            <div>
              {history.map((item) => {
                const st = ITEM_STATUS[item.status];
                return (
                  <LinhaHistorico key={item.id}>
                    <Selo tom={st.tom}>{st.label}</Selo>
                    <span style={{ flex: "1 1 200px", minWidth: 0, overflowWrap: "anywhere" }}>
                      {(item.caption ?? "").slice(0, 80) || "Sem legenda"}
                    </span>
                    <Hint>{formatDate(item.publishedAt ?? item.mediaTimestamp)}</Hint>
                    {item.googleSearchUrl && (
                      <a href={item.googleSearchUrl} target="_blank" rel="noreferrer">
                        Ver no Google
                      </a>
                    )}
                  </LinhaHistorico>
                );
              })}
            </div>
          </Cartao>
        )}
      </Coluna>

      <Aside>
        <AsideCard>
          <Lab>
            <span className="material-icons">info</span>Como funciona
          </Lab>
          <Check $ok>
            <span className="material-icons">check_circle</span>Só lê as publicações; não posta nem lê mensagens
          </Check>
          <Check $ok>
            <span className="material-icons">check_circle</span>Entra só o que for publicado depois de ativar
          </Check>
          <Check $ok>
            <span className="material-icons">check_circle</span>Cada post passa por aprovação antes do Google
          </Check>
          <Check $ok>
            <span className="material-icons">check_circle</span>Telefone, link e hashtag saem do texto
          </Check>
          <Check>
            <span className="material-icons">info</span>Vídeo e Reels ficam de fora; carrossel usa a 1ª foto
          </Check>
        </AsideCard>
        <AsideCard>
          <Lab>
            <span className="material-icons">verified_user</span>Requisitos
          </Lab>
          <Hint>
            Conta profissional do Instagram (Comercial ou Criador de conteúdo) e o perfil do Google
            conectado na GeoRanking. Dá para desconectar a qualquer momento.
          </Hint>
        </AsideCard>
      </Aside>
    </EditorGrid>
  );
};

export default InstagramTab;
